/**
 * 路由配置
 * createBrowserRouter + 懒加载 + 嵌套路由
 * 所有页面组件使用 React.lazy，包裹 Suspense fallback
 */
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import GridOnIcon from '@mui/icons-material/GridOn';
import FolderIcon from '@mui/icons-material/Folder';
import GroupIcon from '@mui/icons-material/Group';
import CategoryIcon from '@mui/icons-material/Category';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import ScienceIcon from '@mui/icons-material/Science';
import BusinessIcon from '@mui/icons-material/Business';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import HandshakeIcon from '@mui/icons-material/Handshake';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import QuizIcon from '@mui/icons-material/Quiz';
import CastForEducationIcon from '@mui/icons-material/CastForEducation';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import { AppShell } from '@/components/layout/AppShell';
import { ServiceLayout } from '@/components/layout/ServiceLayout';
import { ModuleLayout } from '@/components/layout/ModuleLayout';
import { PagePlaceholder } from '@/components/common/PagePlaceholder';
import { AuthGuard } from '@/components/common/AuthGuard';
import { ROUTE_PATHS } from './routes';

/** 统一 Loading 组件 */
function PageLoading(): JSX.Element {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
      }}
    >
      <CircularProgress sx={{ color: '#E53935' }} />
    </Box>
  );
}

// ============================================================
// 合作伙伴页面懒加载
// ============================================================
const LoginPage = lazy(() =>
  import('@/pages/login/LoginPage').then((m) => ({ default: m.LoginPage })),
);
const PartnerApplicationPage = lazy(() =>
  import('@/pages/login/PartnerApplicationPage').then((m) => ({ default: m.PartnerApplicationPage })),
);
const ApplicationSuccessPage = lazy(() =>
  import('@/pages/login/ApplicationSuccessPage').then((m) => ({ default: m.ApplicationSuccessPage })),
);
const ForceChangePasswordPage = lazy(() =>
  import('@/pages/login/ForceChangePasswordPage').then((m) => ({ default: m.ForceChangePasswordPage })),
);
const ForgotPasswordPage = lazy(() =>
  import('@/pages/login/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })),
);
const DashboardPage = lazy(() =>
  import('@/pages/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const TechCertificationsPage = lazy(() =>
  import('@/pages/service-capability/TechCertificationsPage').then((m) => ({
    default: m.TechCertificationsPage,
  })),
);
const CompanySettingsPage = lazy(() =>
  import('@/pages/settings/CompanySettingsPage').then((m) => ({
    default: m.CompanySettingsPage,
  })),
);
const ContactsPage = lazy(() =>
  import('@/pages/settings/ContactsPage').then((m) => ({ default: m.ContactsPage })),
);
const HelpCenterPage = lazy(() =>
  import('@/pages/settings/HelpCenterPage').then((m) => ({ default: m.HelpCenterPage })),
);
// 认证相关页面（4个）
const CertificationResultPage = lazy(() =>
  import('@/pages/settings/CertificationPages').then((m) => ({ default: m.CertificationResultPage })),
);
const CertificationProgressPage = lazy(() =>
  import('@/pages/settings/CertificationPages').then((m) => ({ default: m.CertificationProgressPage })),
);
const AgreementSignPage = lazy(() =>
  import('@/pages/settings/CertificationPages').then((m) => ({ default: m.AgreementSignPage })),
);
const ApplicationFormPage = lazy(() =>
  import('@/pages/settings/CertificationPages').then((m) => ({ default: m.ApplicationFormPage })));
const AlliancePage = lazy(() =>
  import('@/pages/settings/AlliancePage').then((m) => ({ default: m.AlliancePage })),
);

const AnnouncementsPage = lazy(() =>
  import('@/pages/announcements/AnnouncementsPage').then((m) => ({ default: m.default })),
);

const ActivitiesPage = lazy(() =>
  import('@/pages/activities/ActivitiesPage').then((m) => ({ default: m.default })),
);

const HealthDashboardPage = lazy(() =>
  import('@/pages/health/HealthDashboardPage').then((m) => ({ default: m.HealthDashboardPage })),
);

const MdfListPage = lazy(() =>
  import('@/pages/mdf/MdfListPage').then((m) => ({ default: m.MdfListPage })),
);

// ============================================================
// 服务能力页面懒加载（15 个）
// ============================================================
const CapabilityMatrixPage = lazy(() =>
  import('@/pages/service-capability/TechServicePages').then((m) => ({ default: m.CapabilityMatrixPage })),
);
const CaseLibraryPage = lazy(() =>
  import('@/pages/service-capability/TechServicePages').then((m) => ({ default: m.CaseLibraryPage })),
);
const EngineerTeamPage = lazy(() =>
  import('@/pages/service-capability/TechServicePages').then((m) => ({ default: m.EngineerTeamPage })),
);
const SolutionPortfolioPage = lazy(() =>
  import('@/pages/service-capability/SolutionPages').then((m) => ({ default: m.SolutionPortfolioPage })),
);
const SolutionDesignPage = lazy(() =>
  import('@/pages/service-capability/SolutionPages').then((m) => ({ default: m.SolutionDesignPage })),
);
const PocResourcesPage = lazy(() =>
  import('@/pages/service-capability/SolutionPages').then((m) => ({ default: m.PocResourcesPage })),
);
const IndustrySolutionsPage = lazy(() =>
  import('@/pages/service-capability/SolutionPages').then((m) => ({ default: m.IndustrySolutionsPage })),
);
const ComplianceCertificatesPage = lazy(() =>
  import('@/pages/service-capability/CompliancePages').then((m) => ({ default: m.ComplianceCertificatesPage })),
);
const QualityAuditPage = lazy(() =>
  import('@/pages/service-capability/CompliancePages').then((m) => ({ default: m.QualityAuditPage })),
);
const SlaStandardsPage = lazy(() =>
  import('@/pages/service-capability/CompliancePages').then((m) => ({ default: m.SlaStandardsPage })),
);
const UpgradeApplicationPage = lazy(() =>
  import('@/pages/service-capability/CompliancePages').then((m) => ({ default: m.UpgradeApplicationPage })),
);
const LearningMaterialsPage = lazy(() =>
  import('@/pages/service-capability/LearningPages').then((m) => ({ default: m.LearningMaterialsPage })),
);
const ExamBankPage = lazy(() =>
  import('@/pages/service-capability/LearningPages').then((m) => ({ default: m.ExamBankPage })),
);
const CourseManagementPage = lazy(() =>
  import('@/pages/service-capability/LearningPages').then((m) => ({ default: m.CourseManagementPage })),
);
const LearningProgressPage = lazy(() =>
  import('@/pages/service-capability/LearningPages').then((m) => ({ default: m.LearningProgressPage })),
);

// ============================================================
// 项目管理页面懒加载（12 个）
// ============================================================
const NewRegistrationPage = lazy(() =>
  import('@/pages/projects/ProjectPages').then((m) => ({ default: m.NewRegistrationPage })),
);
const RegistrationListPage = lazy(() =>
  import('@/pages/projects/ProjectPages').then((m) => ({ default: m.RegistrationListPage })),
);
const OpportunitiesPage = lazy(() =>
  import('@/pages/projects/ProjectPages').then((m) => ({ default: m.OpportunitiesPage })),
);
const OpportunityAnalysisPage = lazy(() =>
  import('@/pages/projects/ProjectPages').then((m) => ({ default: m.OpportunityAnalysisPage })),
);
const OpportunityRecommendationPage = lazy(() =>
  import('@/pages/projects/ProjectPages').then((m) => ({ default: m.OpportunityRecommendationPage })),
);
const SpecialPricingPage = lazy(() =>
  import('@/pages/projects/ProjectPages').then((m) => ({ default: m.SpecialPricingPage })),
);
const RebateApplicationPage = lazy(() =>
  import('@/pages/projects/ProjectPages').then((m) => ({ default: m.RebateApplicationPage })),
);
const ContractApplicationPage = lazy(() =>
  import('@/pages/projects/ProjectPages').then((m) => ({ default: m.ContractApplicationPage })),
);
const AuthorizationLetterPage = lazy(() =>
  import('@/pages/projects/ProjectPages').then((m) => ({ default: m.AuthorizationLetterPage })),
);
const CommitmentLetterPage = lazy(() =>
  import('@/pages/projects/ProjectPages').then((m) => ({ default: m.CommitmentLetterPage })),
);
const MaterialRequestPage = lazy(() =>
  import('@/pages/projects/ProjectPages').then((m) => ({ default: m.MaterialRequestPage })),
);

// ============================================================
// 业绩与收益页面懒加载（12 个）
// ============================================================
const OverviewPage = lazy(() =>
  import('@/pages/performance/PerformancePages').then((m) => ({ default: m.OverviewPage })),
);
const TrendsPage = lazy(() =>
  import('@/pages/performance/PerformancePages').then((m) => ({ default: m.TrendsPage })),
);
const TargetAchievementPage = lazy(() =>
  import('@/pages/performance/PerformancePages').then((m) => ({ default: m.TargetAchievementPage })),
);
const CommissionDetailsPage = lazy(() =>
  import('@/pages/performance/PerformancePages').then((m) => ({ default: m.CommissionDetailsPage })),
);
const CommissionSettlementPage = lazy(() =>
  import('@/pages/performance/PerformancePages').then((m) => ({ default: m.CommissionSettlementPage })),
);
const CommissionReportPage = lazy(() =>
  import('@/pages/performance/PerformancePages').then((m) => ({ default: m.CommissionReportPage })),
);
const RewardPolicyPage = lazy(() =>
  import('@/pages/performance/PerformancePages').then((m) => ({ default: m.RewardPolicyPage })),
);
const RewardDetailsPage = lazy(() =>
  import('@/pages/performance/PerformancePages').then((m) => ({ default: m.RewardDetailsPage })),
);
const RewardRankingPage = lazy(() =>
  import('@/pages/performance/PerformancePages').then((m) => ({ default: m.RewardRankingPage })),
);
const ProjectAnalysisPage = lazy(() =>
  import('@/pages/performance/PerformancePages').then((m) => ({ default: m.ProjectAnalysisPage })),
);
const CustomerAnalysisPage = lazy(() =>
  import('@/pages/performance/PerformancePages').then((m) => ({ default: m.CustomerAnalysisPage })),
);
const RegionAnalysisPage = lazy(() =>
  import('@/pages/performance/PerformancePages').then((m) => ({ default: m.RegionAnalysisPage })),
);

// ============================================================
// 资源中心页面懒加载（14 个）
// ============================================================
const ProductBrochuresPage = lazy(() =>
  import('@/pages/resources/ResourcePages').then((m) => ({ default: m.ProductBrochuresPage })),
);
const PromotionalVideosPage = lazy(() =>
  import('@/pages/resources/ResourcePages').then((m) => ({ default: m.PromotionalVideosPage })),
);
const PostersPage = lazy(() =>
  import('@/pages/resources/ResourcePages').then((m) => ({ default: m.PostersPage })),
);
const SuccessCasesPage = lazy(() =>
  import('@/pages/resources/ResourcePages').then((m) => ({ default: m.SuccessCasesPage })),
);
const WhitepapersPage = lazy(() =>
  import('@/pages/resources/ResourcePages').then((m) => ({ default: m.WhitepapersPage })),
);
const TechSpecsPage = lazy(() =>
  import('@/pages/resources/ResourcePages').then((m) => ({ default: m.TechSpecsPage })),
);
const ApiDocsPage = lazy(() =>
  import('@/pages/resources/ResourcePages').then((m) => ({ default: m.ApiDocsPage })),
);
const CertTrainingPage = lazy(() =>
  import('@/pages/resources/ResourcePages').then((m) => ({ default: m.CertTrainingPage })),
);
const ProductTrainingPage = lazy(() =>
  import('@/pages/resources/ResourcePages').then((m) => ({ default: m.ProductTrainingPage })),
);
const SalesTrainingPage = lazy(() =>
  import('@/pages/resources/ResourcePages').then((m) => ({ default: m.SalesTrainingPage })),
);
const ContractTemplatesPage = lazy(() =>
  import('@/pages/resources/ResourcePages').then((m) => ({ default: m.ContractTemplatesPage })),
);
const AgreementTemplatesPage = lazy(() =>
  import('@/pages/resources/ResourcePages').then((m) => ({ default: m.AgreementTemplatesPage })),
);
const LegalDocsPage = lazy(() =>
  import('@/pages/resources/ResourcePages').then((m) => ({ default: m.LegalDocsPage })),
);

// ============================================================
// 配置报价器页面懒加载（14 个）
// ============================================================
const ServerStoragePage = lazy(() =>
  import('@/pages/quotation/QuotationPages').then((m) => ({ default: m.ServerStoragePage })),
);
const NetworkDevicesPage = lazy(() =>
  import('@/pages/quotation/QuotationPages').then((m) => ({ default: m.NetworkDevicesPage })),
);
const SecurityDevicesPage = lazy(() =>
  import('@/pages/quotation/QuotationPages').then((m) => ({ default: m.SecurityDevicesPage })),
);
const SoftwareProductsPage = lazy(() =>
  import('@/pages/quotation/QuotationPages').then((m) => ({ default: m.SoftwareProductsPage })),
);
const MyOrdersPage = lazy(() =>
  import('@/pages/quotation/MyOrdersPage').then((m) => ({ default: m.MyOrdersPage })),
);
const QuoteListPage = lazy(() =>
  import('@/pages/quotation/QuoteListPageNew').then((m) => ({ default: m.QuoteListPageNew })),
);
const SharedQuotesPage = lazy(() =>
  import('@/pages/quotation/SharedQuotesPageNew').then((m) => ({ default: m.SharedQuotesPageNew })),
);
const QuoteTemplatesPage = lazy(() =>
  import('@/pages/quotation/QuoteTemplatesPageNew').then((m) => ({ default: m.QuoteTemplatesPageNew })),
);
const TrashPage = lazy(() =>
  import('@/pages/quotation/TrashPage').then((m) => ({ default: m.TrashPage })),
);
const TrashSettingsPage = lazy(() =>
  import('@/pages/quotation/TrashSettingsPage').then((m) => ({ default: m.TrashSettingsPage })),
);
const RestoreHistoryPage = lazy(() =>
  import('@/pages/quotation/RestoreHistoryPage').then((m) => ({ default: m.RestoreHistoryPage })),
);
const ExportQuotePage = lazy(() =>
  import('@/pages/quotation/ExportCenterPage').then((m) => ({ default: m.ExportCenterPage })),
);

/** Suspense 包装器 */
function withSuspense(element: React.ReactNode): JSX.Element {
  return <Suspense fallback={<PageLoading />}>{element}</Suspense>;
}

/** 创建服务能力 P1 占位路由元素 */
function placeholder(title: string, icon: React.ReactNode): JSX.Element {
  return withSuspense(
    <PagePlaceholder title={title} description="功能开发中，敬请期待" icon={icon} />,
  );
}

/** 路由配置 */
export const router = createBrowserRouter([
  // ============================================================
  // 登录页（无需认证）
  // ============================================================
  {
    path: '/login',
    element: withSuspense(<LoginPage />),
  },
  // ============================================================
  // 合作伙伴申请页（无需认证）
  // ============================================================
  {
    path: '/partner-apply',
    element: withSuspense(<PartnerApplicationPage />),
  },
  {
    path: '/apply-success',
    element: withSuspense(<ApplicationSuccessPage />),
  },
  // 强制修改密码页（需已登录但未完成改密）
  {
    path: '/change-password',
    element: withSuspense(<ForceChangePasswordPage />),
  },
  // 忘记密码页（无需认证）
  {
    path: '/forgot-password',
    element: withSuspense(<ForgotPasswordPage />),
  },
  // 帮助中心（无需认证）
  {
    path: '/settings/help',
    element: withSuspense(<HelpCenterPage />),
  },
  // ============================================================
  // 所有功能页面（需认证）
  // ============================================================
  {
    path: '/',
    element: <AuthGuard />,
    children: [
  {
    path: ROUTE_PATHS.HOME,
    element: <AppShell />,
    children: [
      { index: true, element: withSuspense(<DashboardPage />) },
    ],
  },
  {
    path: ROUTE_PATHS.SERVICE_CAPABILITY,
    element: <AppShell />,
    children: [
      {
        path: '',
        element: <ServiceLayout />,
        children: [
          { index: true, element: <Navigate to={ROUTE_PATHS.TECH_CERTIFICATIONS} replace /> },
          { path: 'tech-certifications', element: withSuspense(<TechCertificationsPage />) },
          { path: 'capability-matrix', element: withSuspense(<CapabilityMatrixPage />) },
          { path: 'case-library', element: withSuspense(<CaseLibraryPage />) },
          { path: 'engineer-team', element: withSuspense(<EngineerTeamPage />) },
          { path: 'solution-portfolio', element: withSuspense(<SolutionPortfolioPage />) },
          { path: 'solution-design', element: withSuspense(<SolutionDesignPage />) },
          { path: 'poc-resources', element: withSuspense(<PocResourcesPage />) },
          { path: 'industry-solutions', element: withSuspense(<IndustrySolutionsPage />) },
          { path: 'compliance-certificates', element: withSuspense(<ComplianceCertificatesPage />) },
          { path: 'quality-audit', element: withSuspense(<QualityAuditPage />) },
          { path: 'sla-standards', element: withSuspense(<SlaStandardsPage />) },
          { path: 'upgrade-application', element: withSuspense(<UpgradeApplicationPage />) },
          { path: 'learning-materials', element: withSuspense(<LearningMaterialsPage />) },
          { path: 'exam-bank', element: withSuspense(<ExamBankPage />) },
          { path: 'course-management', element: withSuspense(<CourseManagementPage />) },
          { path: 'learning-progress', element: withSuspense(<LearningProgressPage />) },
        ],
      },
    ],
  },
  // ============================================================
  // 项目管理路由
  // ============================================================
  {
    path: ROUTE_PATHS.PROJECTS,
    element: <AppShell />,
    children: [
      {
        path: '',
        element: <ModuleLayout module="projects" title="项目管理" />,
        children: [
          { index: true, element: <Navigate to={ROUTE_PATHS.PROJECTS_NEW_REGISTRATION} replace /> },
          { path: 'new-registration', element: withSuspense(<NewRegistrationPage />) },
          { path: 'registration-list', element: withSuspense(<RegistrationListPage />) },
          { path: 'opportunities', element: withSuspense(<OpportunitiesPage />) },
          { path: 'opportunity-analysis', element: withSuspense(<OpportunityAnalysisPage />) },
          { path: 'opportunity-recommendation', element: withSuspense(<OpportunityRecommendationPage />) },
          { path: 'special-pricing', element: withSuspense(<SpecialPricingPage />) },
          { path: 'rebate-application', element: withSuspense(<RebateApplicationPage />) },
          { path: 'contract-application', element: withSuspense(<ContractApplicationPage />) },
          { path: 'authorization-letter', element: withSuspense(<AuthorizationLetterPage />) },
          { path: 'commitment-letter', element: withSuspense(<CommitmentLetterPage />) },
          { path: 'material-request', element: withSuspense(<MaterialRequestPage />) },
        ],
      },
    ],
  },
  // ============================================================
  // 业绩与收益路由
  // ============================================================
  {
    path: ROUTE_PATHS.PERFORMANCE,
    element: <AppShell />,
    children: [
      {
        path: '',
        element: <ModuleLayout module="performance" title="业绩与收益" />,
        children: [
          { index: true, element: <Navigate to={ROUTE_PATHS.PERFORMANCE_OVERVIEW} replace /> },
          { path: 'overview', element: withSuspense(<OverviewPage />) },
          { path: 'trends', element: withSuspense(<TrendsPage />) },
          { path: 'target-achievement', element: withSuspense(<TargetAchievementPage />) },
          { path: 'commission-details', element: withSuspense(<CommissionDetailsPage />) },
          { path: 'commission-settlement', element: withSuspense(<CommissionSettlementPage />) },
          { path: 'commission-report', element: withSuspense(<CommissionReportPage />) },
          { path: 'reward-policy', element: withSuspense(<RewardPolicyPage />) },
          { path: 'reward-details', element: withSuspense(<RewardDetailsPage />) },
          { path: 'reward-ranking', element: withSuspense(<RewardRankingPage />) },
          { path: 'project-analysis', element: withSuspense(<ProjectAnalysisPage />) },
          { path: 'customer-analysis', element: withSuspense(<CustomerAnalysisPage />) },
          { path: 'region-analysis', element: withSuspense(<RegionAnalysisPage />) },
        ],
      },
    ],
  },
  // ============================================================
  // 资源中心路由
  // ============================================================
  {
    path: ROUTE_PATHS.RESOURCES,
    element: <AppShell />,
    children: [
      {
        path: '',
        element: <ModuleLayout module="resources" title="资源中心" />,
        children: [
          { index: true, element: <Navigate to={ROUTE_PATHS.RESOURCES_PRODUCT_BROCHURES} replace /> },
          { path: 'sales', element: <Navigate to={ROUTE_PATHS.RESOURCES_PRODUCT_BROCHURES} replace /> },
          { path: 'product-brochures', element: withSuspense(<ProductBrochuresPage />) },
          { path: 'promotional-videos', element: withSuspense(<PromotionalVideosPage />) },
          { path: 'posters', element: withSuspense(<PostersPage />) },
          { path: 'success-cases', element: withSuspense(<SuccessCasesPage />) },
          { path: 'whitepapers', element: withSuspense(<WhitepapersPage />) },
          { path: 'tech-specs', element: withSuspense(<TechSpecsPage />) },
          { path: 'api-docs', element: withSuspense(<ApiDocsPage />) },
          { path: 'cert-training', element: withSuspense(<CertTrainingPage />) },
          { path: 'product-training', element: withSuspense(<ProductTrainingPage />) },
          { path: 'sales-training', element: withSuspense(<SalesTrainingPage />) },
          { path: 'contract-templates', element: withSuspense(<ContractTemplatesPage />) },
          { path: 'agreement-templates', element: withSuspense(<AgreementTemplatesPage />) },
          { path: 'legal-docs', element: withSuspense(<LegalDocsPage />) },
        ],
      },
    ],
  },
  // ============================================================
  // 配置报价器路由
  // ============================================================
  {
    path: ROUTE_PATHS.QUOTATION,
    element: <AppShell />,
    children: [
      {
        path: '',
        element: <ModuleLayout module="quotation" title="配置报价器" />,
        children: [
          { index: true, element: <Navigate to={ROUTE_PATHS.QUOTATION_SERVER_STORAGE} replace /> },
          { path: 'server-storage', element: withSuspense(<ServerStoragePage />) },
          { path: 'network-devices', element: withSuspense(<NetworkDevicesPage />) },
          { path: 'security-devices', element: withSuspense(<SecurityDevicesPage />) },
          { path: 'software-products', element: withSuspense(<SoftwareProductsPage />) },
          { path: 'my-orders', element: withSuspense(<MyOrdersPage />) },
          { path: 'quote-list', element: withSuspense(<QuoteListPage />) },
          { path: 'shared-quotes', element: withSuspense(<SharedQuotesPage />) },
          { path: 'quote-templates', element: withSuspense(<QuoteTemplatesPage />) },
          { path: 'trash', element: withSuspense(<TrashPage />) },
          { path: 'trash-settings', element: withSuspense(<TrashSettingsPage />) },
          { path: 'restore-history', element: withSuspense(<RestoreHistoryPage />) },
          { path: 'export-quote', element: withSuspense(<ExportQuotePage />) },
          { path: 'export-history', element: withSuspense(<ExportQuotePage />) },
          { path: 'export-templates', element: withSuspense(<ExportQuotePage />) },
        ],
      },
    ],
  },
  {
    path: '/settings',
    element: <AppShell />,
    children: [
      { index: true, element: <Navigate to={ROUTE_PATHS.SETTINGS_COMPANY} replace /> },
      { path: 'company', element: withSuspense(<CompanySettingsPage />) },
      { path: 'contacts', element: withSuspense(<ContactsPage />) },
    ],
  },
  {
    path: '/certification',
    element: <AppShell />,
    children: [
      { path: 'result', element: withSuspense(<CertificationResultPage />) },
      { path: 'progress', element: withSuspense(<CertificationProgressPage />) },
      { path: 'agreement', element: withSuspense(<AgreementSignPage />) },
      { path: 'application', element: withSuspense(<ApplicationFormPage />) },
    ],
  },
  {
    path: '/alliance',
    element: <AppShell />,
    children: [
      { index: true, element: withSuspense(<AlliancePage />) },
    ],
  },
  {
    path: '/announcements',
    element: <AppShell />,
    children: [
      { index: true, element: <Navigate to={ROUTE_PATHS.ANNOUNCEMENTS_POLICIES} replace /> },
      { path: 'policies', element: withSuspense(<AnnouncementsPage />) },
    ],
  },
  {
    path: '/events',
    element: <AppShell />,
    children: [
      { index: true, element: <Navigate to={ROUTE_PATHS.EVENTS_MARKETING} replace /> },
      { path: 'marketing', element: withSuspense(<ActivitiesPage />) },
    ],
  },
  {
    path: '/health',
    element: <AppShell />,
    children: [
      {
        path: '',
        element: <ServiceLayout />,
        children: [
          { index: true, element: withSuspense(<HealthDashboardPage />) },
        ],
      },
    ],
  },
  {
    path: '/mdf',
    element: <AppShell />,
    children: [
      {
        path: '',
        element: <ServiceLayout />,
        children: [
          { index: true, element: withSuspense(<MdfListPage />) },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to={ROUTE_PATHS.HOME} replace />,
  },
    ],
  },
]);

